import { useState, useEffect } from 'react';
import { Gauge, ArrowUpCircle, Mail } from 'lucide-react';
import Modal from './Modal';
import Button from './Button';
import { useTranslation } from '../../context/LanguageContext';
import { deciderVente, libelleCompteur } from '../../utils/ventePalier';
import { getTarifsUnlockingModule } from '../../services/tarifsService';
import { getActiveSubscriptions } from '../../services/paymentService';

/**
 * Remplace {clef} par sa valeur dans un libellé traduit.
 */
function remplir(gabarit, valeurs) {
    if (!gabarit) return '';
    return Object.keys(valeurs).reduce(
        (texte, clef) => texte.replace(`{${clef}}`, valeurs[clef]),
        gabarit
    );
}

/**
 * Le nom du tarif dans la langue courante.
 */
function nomDuTarif(tarif, langue) {
    if (!tarif) return '';
    const cles = [`name_${langue}`, `NAME_${String(langue).toUpperCase()}`, 'name_en', 'NAME_EN', 'code', 'CODE'];
    for (const c of cles) {
        const v = tarif[c];
        if (v && String(v).trim()) return String(v).trim();
    }
    return '';
}

function prixMensuel(tarif) {
    const brut = tarif?.monthlyprice ?? tarif?.monthlyPrice ?? tarif?.MONTHLYPRICE;
    const n = Number(brut);
    if (!Number.isFinite(n)) return null;
    return n.toFixed(2).replace('.', ',');
}

/**
 * L'écran qui s'affiche quand un plafond est atteint.
 *
 * C'EST LE PRINCIPAL POINT D'ENTREE DE LA VENTE — la spec le dit : le
 * declencheur est la limite atteinte, pas l'inscription. Au moment de
 * s'inscrire, personne ne sait encore ce qu'est la limite.
 *
 * Trois partis pris :
 *
 * 1. LE CHIFFRE D'ABORD. « 20 sur 20 » se comprend ; « limite atteinte »
 *    laisse deviner. La jauge montre que le plafond est une mesure, pas
 *    une sanction.
 *
 * 2. LA LIMITE EST NORMALE, PAS UNE FAUTE. En Gratuit tout est ouvert
 *    a tous, avec un plafond : c'est le modele, et le dire evite que la
 *    personne se croie penalisee.
 *
 * 3. ON NE PROPOSE QUE CE QUI DEPLAFONNE REELLEMENT. deciderVente
 *    s'appuie sur les paliers rendus par /unlocking, propres au type de
 *    profil. Un artisan ne se voit proposer que FULL. Et si rien ne
 *    deplafonne, on ne vend rien : on le dit, et on ouvre le contact.
 *
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {string} moduleCode - Le module dont la limite est atteinte
 * @param {number} current - Le compte actuel
 * @param {number} max - Le plafond
 * @param {number} profileTypeId - Le type de profil de la personne
 * @param {string} peopleUuid - Pour lire ses abonnements actifs
 * @param {Function} onNavigate - Pour ouvrir l'ecran d'abonnement
 * @param {string} periodUnit - day | week | month | year : la limite porte sur cette fenetre glissante ; vide = total
 * @param {number} periodCount - Taille de la fenetre
 * @param {boolean} feature - Vrai quand ce n'est pas un plafond mais une FONCTION reservee aux abonnements payants : pas de jauge, un autre texte
 */
const LimitReachedModal = ({
    isOpen,
    onClose,
    moduleCode,
    current,
    max,
    profileTypeId,
    peopleUuid,
    onNavigate,
    periodUnit = '',
    periodCount = 1,
    feature = false
}) => {
    const { t, language } = useTranslation();
    const [decision, setDecision] = useState({ action: 'rien', tarifPropose: null, tarifDetenu: null });
    const [chargement, setChargement] = useState(false);

    useEffect(() => {
        if (!isOpen || !moduleCode || !profileTypeId) return;

        let annule = false;
        const charger = async () => {
            setChargement(true);
            try {
                const [paliers, abonnements] = await Promise.all([
                    getTarifsUnlockingModule(profileTypeId, moduleCode),
                    peopleUuid ? getActiveSubscriptions(peopleUuid) : Promise.resolve([])
                ]);
                if (annule) return;
                setDecision(deciderVente(paliers, abonnements));
            } catch (e) {
                // Ne rien proposer plutot qu'une offre construite sur des
                // donnees incompletes : mieux vaut un ecran qui explique la
                // limite qu'un abonnement qui ne la leverait pas.
                console.error('[LimitReachedModal] Chargement des paliers impossible:', e);
                if (!annule) setDecision({ action: 'rien', tarifPropose: null, tarifDetenu: null });
            } finally {
                if (!annule) setChargement(false);
            }
        };

        charger();
        return () => { annule = true; };
    }, [isOpen, moduleCode, profileTypeId, peopleUuid]);

    const libelles = t.limitReached || {};
    const compteur = libelleCompteur(current, max);
    const cleFenetre = { day: 'windowDay', week: 'windowWeek', month: 'windowMonth', year: 'windowYear' }[periodUnit];
    const fenetre = cleFenetre ? remplir(libelles[cleFenetre], { n: periodCount }) : '';
    const nomPropose = nomDuTarif(decision.tarifPropose, language);
    const nomDetenu = nomDuTarif(decision.tarifDetenu, language);
    const prix = prixMensuel(decision.tarifPropose);

    const ouvrirAbonnement = () => {
        onClose?.();
        onNavigate?.('preferences', { tab: 'subscription' });
    };

    const pourcentage = (() => {
        const n = Number(current);
        const m = Number(max);
        if (!Number.isFinite(n) || !Number.isFinite(m) || m <= 0) return 100;
        return Math.min(100, Math.round((n / m) * 100));
    })();

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={feature ? libelles.featureTitle : libelles.title}
            icon={Gauge}
            size="sm"
            hideFooter
        >
            <div className="flex flex-col items-center text-center">
                {/* Le chiffre d'abord : c'est lui qui se comprend sans phrase. */}
                {!feature && compteur && (
                    <>
                        <p className="text-3xl font-semibold text-gray-900 tabular-nums">
                            {remplir(libelles.counter, { current, max })}
                        </p>
                        {fenetre && (
                            <p className="mt-1 text-sm text-gray-500" data-testid="limite-fenetre">{fenetre}</p>
                        )}
                        <div className="w-full max-w-[240px] h-1.5 mt-3 mb-5 rounded-full bg-gray-100 overflow-hidden">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-cabaza-purple to-cabaza-pink"
                                style={{ width: `${pourcentage}%` }}
                            />
                        </div>
                    </>
                )}

                {/* La limite est le modèle, pas une sanction. */}
                <p className="text-sm text-gray-600 max-w-sm">
                    {feature ? libelles.explainFeature : libelles.explainBeta}
                </p>

                {decision.action !== 'rien' && (
                    <p className="mt-1 text-sm text-gray-600 max-w-sm">
                        {libelles.explainUpgrade}
                    </p>
                )}

                {/* Ce qu'elle paie déjà, pour que la proposition se situe. */}
                {decision.action === 'upgrader' && nomDetenu && (
                    <p className="mt-4 text-xs text-gray-500">
                        {remplir(libelles.currentPlan, { tarif: nomDetenu })}
                    </p>
                )}

                <div className="w-full mt-6 space-y-2">
                    {chargement && (
                        <div className="h-11 rounded-xl bg-gray-100 animate-pulse" />
                    )}

                    {!chargement && decision.action !== 'rien' && (
                        <Button variant="primary" onClick={ouvrirAbonnement} className="w-full">
                            <span className="inline-flex items-center justify-center gap-2">
                                {decision.action === 'upgrader' && <ArrowUpCircle className="w-4 h-4" />}
                                {remplir(
                                    decision.action === 'upgrader' ? libelles.upgrade : libelles.buy,
                                    { tarif: nomPropose }
                                )}
                                {prix !== null && (
                                    <span className="opacity-80">
                                        · {remplir(libelles.perMonth, { price: prix })}
                                    </span>
                                )}
                            </span>
                        </Button>
                    )}

                    {/* Aucun palier ne déplafonne ce module pour ce type de
                        profil : le dire, et ouvrir le contact. Proposer un
                        abonnement qui ne changerait rien serait pire. */}
                    {!chargement && decision.action === 'rien' && (
                        <>
                            <p className="text-sm text-gray-500 max-w-sm mx-auto">
                                {libelles.noOffer}
                            </p>
                            <Button
                                variant="secondary"
                                onClick={() => { onClose?.(); onNavigate?.('support'); }}
                                className="w-full"
                            >
                                <span className="inline-flex items-center justify-center gap-2">
                                    <Mail className="w-4 h-4" />
                                    {libelles.contact}
                                </span>
                            </Button>
                        </>
                    )}

                    <Button variant="ghost" onClick={onClose} className="w-full">
                        {libelles.close}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default LimitReachedModal;
